import React, { useCallback, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';

import {
  Box,
  Button,
  Paper,
  Rating,
  TextareaAutosize,
  Typography,
} from '@material-ui/core';

import AddAPhotoIcon from '@material-ui/icons/AddAPhoto';

import { useHistory, useParams } from 'react-router-dom';
import { useAPI } from '../../utils/api';
import { useDialog } from '../../utils/dialog';
import { MODE } from '../../utils/mode';

const UploadedImagePreview = ({ image, onDelete, isReadOnly }) => {
  return image ? (
    <>
      <Typography variant="body2" mb={1}>
        圖片預覽
      </Typography>
      <Box
        sx={{
          width: 'fit-content',
          borderRadius: 2,
          border: '1px solid #eaeaea',
          padding: 1,
          boxSizing: 'border-box',
        }}
        onClick={isReadOnly ? undefined : () => onDelete()}
      >
        <img
          style={{
            maxHeight: '240px',
          }}
          src={typeof image === 'string' ? image : image.preview}
          title="首頁蓋板"
        />
      </Box>
      {isReadOnly ? undefined : (
        <Typography variant="body2" mb={1}>
          (點擊照片以刪除)
        </Typography>
      )}
    </>
  ) : (
    ''
  );
};

UploadedImagePreview.propTypes = {
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onDelete: PropTypes.func,
  isReadOnly: PropTypes.bool,
};

UploadedImagePreview.defaultProps = {
  onDelete: () => {},
  isReadOnly: false,
};

const RecordCommentPage = () => {
  const { mode, orderID } = useParams();
  const api = useAPI();
  const dialog = useDialog();
  const history = useHistory();

  const [stars, setStars] = useState(0);

  const [isUnmounted, setIsUnmounted] = useState(false);
  const [commentTemplates, setCommentTemplates] = useState([]);
  const [comment, setComment] = useState('');
  const [shouldCommentUpdate, setShouldCommentUpdate] = useState(false);
  const [importedFiles, setImportedFiles] = useState();
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');
  const [storedCommentData, setStoredCommentData] = useState();
  const [shouldPreviewUpdate, setShouldPreviewUpdate] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [caseImageNotFound, setCaseImageNotFount] = useState(false);
  const caseImageReference = useRef(null);

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (fileRejections.length === 0) {
      // Success
      setUploadErrorMessage('');
      setImportedFiles(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        )
      );
    } else if (fileRejections.length > 1) {
      setImportedFiles();
      setUploadErrorMessage('一次只能上傳一個檔案');
    } else {
      setImportedFiles();
      const rejectedFile = fileRejections[0];
      const rejectedReasons = rejectedFile.errors;
      const invalidTypeError = rejectedReasons.some(
        (error) => error.code === 'file-invalid-type'
      );
      if (invalidTypeError) {
        setUploadErrorMessage('只可接受圖片');
      } else {
        setUploadErrorMessage('上傳檔案時發生錯誤');
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    maxFiles: 1,
    accept: 'image/*',
  });

  const saveComment = (event) => {
    event.preventDefault();
    const form = event.target;
    const formData = new FormData(form);
    const uploadedImage = formData.get('uploadedImage');
    const reader = new FileReader();
    reader.onloadend = () => {
      const data = {
        commentID: mode === 'new' ? 0 : storedCommentData.id,
        orderID: orderID,
        commentStars: stars,
        commentText: formData.get('commentText'),
        pictureString: '',
        pictureName: '',
      };

      if (importedFiles && importedFiles.length > 0) {
        data.pictureString = reader.result;
        data.pictureName = formData.get('uploadedImage').name;
      } else if (
        mode === MODE.EDIT &&
        storedCommentData &&
        storedCommentData.pictureString
      ) {
        data.pictureString = storedCommentData.pictureString;
        data.pictureName = storedCommentData.pictureName;
      }

      api
        .saveComment(data)
        .then((success) => {
          if (!isUnmounted) {
            if (!success) {
              dialog.openAlert({ message: '傳送資料失敗' });
            } else {
              dialog.openAlert({ message: '上傳成功' });
              history.goBack();
            }
          }
        })
        .catch(() => {});
    };

    reader.readAsDataURL(uploadedImage);
  };

  const updateCommentByTemplate = (text) => {
    if (mode === MODE.EDIT) {
      const newData = storedCommentData;
      newData.commentText = text;
      setStoredCommentData(newData);
    } else {
      setComment(text);
    }
    setShouldCommentUpdate(true);
  };

  const deleteStoredImage = () => {
    setImportedFiles();
    if (!storedCommentData) return;
    const newData = storedCommentData;
    newData.pictureName = '';
    newData.pictureString = '';
    setStoredCommentData(newData);
    setShouldPreviewUpdate(true);
  };

  const sessionToken = sessionStorage.getItem('st');

  useEffect(() => {
    setCommentTemplates([
      '很棒的配送品質！',
      '超棒的服務！',
      '超快的配送速度！',
    ]);

    if (mode === MODE.EDIT) {
      api
        .getComment({ id: orderID })
        .then((result) => {
          setStoredCommentData(result);
        })
        .catch(() => {});
    } else if (mode === MODE.NEW) {
      //
    } else {
      dialog.openAlert({ message: '輸入參數錯誤', redirectTo: '/' });
    }

    api
      .fetchCaseImage(sessionToken, orderID)
      .then((resultData) => {
        caseImageReference.current.setAttribute('src', resultData);
      })
      .catch(() => {
        setCaseImageNotFount(true);
      });

    return () => {
      setIsUnmounted(true);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setShouldCommentUpdate(false);
  }, [shouldCommentUpdate]);

  useEffect(() => {
    setShouldPreviewUpdate(false);
  }, [shouldPreviewUpdate]);

  useEffect(() => {
    if (mode === MODE.EDIT && storedCommentData) {
      setStars(storedCommentData.rating);

      if (storedCommentData.editTimes > 0) {
        setIsReadOnly(true);
      }
    }
  }, [mode, storedCommentData]);

  return (
    <>
      <Box
        component="form"
        noValidate
        onSubmit={saveComment}
        sx={{
          padding: { xs: 1, sm: 2, md: 3, lg: 4 },
          maxWidth: '900px',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        <Paper
          elevation={4}
          sx={{
            width: '100%',
            padding: 2,
            marginBottom: 4,
          }}
        >
          <Box marginRight={4} marginBottom={2}>
            <Typography variant="h6" display="inline" fontWeight="bold">
              訂單編號：
            </Typography>
            <Typography variant="h6" display="inline" mr="2px">
              {orderID}
            </Typography>
            {isReadOnly ? (
              <Typography variant="body2" display="inline" color="#aaaaaa">
                (不可編輯)
              </Typography>
            ) : undefined}
          </Box>
          <Box display="flex" justifyContent="center" mb={2}>
            <Rating
              name="commentStars"
              value={stars}
              size="large"
              onChange={(event, newValue) => {
                setStars(newValue);
              }}
              readOnly={isReadOnly}
            />
          </Box>
          <Box mb={1}>
            {!shouldCommentUpdate ? (
              <TextareaAutosize
                name="commentText"
                aria-label="commentText"
                minRows={5}
                placeholder="留言告訴別人您對此次訂單的配送評價吧！"
                defaultValue={
                  mode === MODE.EDIT && storedCommentData
                    ? storedCommentData.commentText
                    : comment
                }
                style={{
                  border: '1px solid #777',
                  borderRadius: '4px',
                  padding: '8px',
                  width: '100%',
                }}
                readOnly={isReadOnly}
              />
            ) : undefined}
          </Box>
          {isReadOnly ? undefined : (
            <Box mb={2}>
              {commentTemplates.map((text) => (
                <Button
                  key={text}
                  type="button"
                  variant="contained"
                  color="info"
                  sx={{ color: 'white', marginRight: 2, marginBottom: 1 }}
                  onClick={() => updateCommentByTemplate(text)}
                >
                  {text}
                </Button>
              ))}
            </Box>
          )}
          <fieldset
            style={{
              marginBottom: '1rem',
              border: 'solid 1px',
              padding: '1rem 0',
              borderRadius: '5px',
            }}
          >
            <legend
              style={{
                marginLeft: '1rem',
                padding: '0 0.5rem',
              }}
            >
              <b>簽單影像</b>
            </legend>
            <div
              style={{
                width: '80%',
                margin: '0 auto',
              }}
            >
              {caseImageNotFound ? (
                <span>暫時無法取得契約單影像或尚未上傳</span>
              ) : (
                <img src="" ref={caseImageReference} />
              )}
            </div>
          </fieldset>
          <Box
            mb={2}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            {!shouldPreviewUpdate ? (
              importedFiles && importedFiles.length > 0 ? (
                <UploadedImagePreview
                  image={importedFiles[0]}
                  onDelete={() => deleteStoredImage()}
                  isReadOnly={isReadOnly}
                />
              ) : mode === MODE.EDIT ? (
                <UploadedImagePreview
                  image={
                    storedCommentData ? storedCommentData.pictureString : ''
                  }
                  onDelete={() => deleteStoredImage()}
                  isReadOnly={isReadOnly}
                />
              ) : undefined
            ) : undefined}
          </Box>
          {isReadOnly ? undefined : (
            <>
              <Box
                {...getRootProps()}
                sx={{
                  minWidth: '240px',
                  width: { xs: '100%', sm: '360px' },
                  height: '200px',
                  border: '1px dashed #bbbbbb',
                  padding: 3,
                  borderRadius: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  color: '#777777',
                  marginBottom: 2,
                }}
              >
                <input
                  id="uploadedImage"
                  name="uploadedImage"
                  {...getInputProps()}
                />
                {isDragActive ? (
                  <Box>放開以上傳照片</Box>
                ) : (
                  <>
                    <Box className="text-center">
                      <AddAPhotoIcon
                        sx={{
                          fontSize: { xs: 60, sm: 72 },
                          color: '#aaaaaa',
                          mb: 1,
                        }}
                      />
                      <Box>
                        {importedFiles && importedFiles.length > 0
                          ? '重新上傳照片'
                          : '新增照片'}
                        <Typography variant="body2">
                          (點擊或拖曳照片至此)
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
              <Typography variant="body2" color="error">
                {uploadErrorMessage}
              </Typography>
              <Typography variant="body2" marginTop={2} marginBottom={2}>
                注意事項: 【最多上傳30個字和1張圖片】
              </Typography>
            </>
          )}
        </Paper>

        {isReadOnly ? (
          <Box display="flex" justifyContent="center">
            <Button
              type="button"
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => history.goBack()}
              sx={{ maxWidth: '160px' }}
            >
              返回
            </Button>
          </Box>
        ) : (
          <Box
            display="grid"
            gridTemplateColumns="1fr 1fr"
            maxWidth="360px"
            columnGap={2}
            ml="auto"
            mr="auto"
          >
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="success"
              sx={{ color: 'white' }}
            >
              儲存
            </Button>
            <Button
              type="button"
              fullWidth
              variant="contained"
              color="error"
              onClick={() => history.goBack()}
            >
              取消
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};

export default RecordCommentPage;
